/**
 * SystemBuilderState - Pure state management class for system builder
 *
 * Manages the current state of the system builder including:
 * - Current subsystem and layer indices
 * - Product selections within groups
 * - Navigation state across layers
 *
 * Dispatches STATE_CHANGED events on mutations via window.BAO.eventBus
 *
 * @class
 */
class SystemBuilderState {
  /**
   * @type {number}
   */
  subSystemIndex = 0

  /**
   * @type {number}
   */
  layerIndex = 0

  /**
   * @type {number}
   */
  flatLayerIndex = 0

  /**
   * @type {Object}
   */
  data = null

  /**
   * @type {number}
   */
  totalLayers = 0

  /**
   * @type {Set<string>}
   */
  dismissedTooltips = new Set()

  /**
   * Creates a new SystemBuilderState instance
   *
   * @param {Object} data - Parsed JSON data containing subSystems structure
   */
  constructor (data) {
    this.data = data
    this.totalLayers = this.getTotalLayerCount()
  }

  /**
   * Calculates the total number of layers across all subsystems
   *
   * @returns {number} Total count of layers
   */
  getTotalLayerCount () {
    let count = 0
    this.data.subSystems.forEach((subSystem) => {
      count += subSystem.layers.length
    })
    return count
  }

  /**
   * Converts subsystem and layer indices to a flat layer index
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @param {number} layerIndex - Index of the layer within the subsystem
   * @returns {number} Flat index across all layers
   */
  getFlatLayerIndex (subSystemIndex, layerIndex) {
    let flatIndex = 0
    for (let i = 0; i < subSystemIndex; i++) {
      flatIndex += this.data.subSystems[i].layers.length
    }
    return flatIndex + layerIndex
  }

  /**
   * Gets a subsystem by index
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @returns {Object|null} Subsystem object or null if not found
   */
  getSubSystem (subSystemIndex) {
    return this.data.subSystems[subSystemIndex] || null
  }

  /**
   * Gets a layer by subsystem and layer indices
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @param {number} layerIndex - Index of the layer within the subsystem
   * @returns {Object|null} Layer object or null if not found
   */
  getLayer (subSystemIndex, layerIndex) {
    const subSystem = this.getSubSystem(subSystemIndex)
    if (!subSystem) return null

    return subSystem.layers[layerIndex] || null
  }

  /**
   * Gets a group by subsystem, layer, and group indices
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @param {number} layerIndex - Index of the layer within the subsystem
   * @param {number} groupIndex - Index of the group within the layer
   * @returns {Object|null} Group object or null if not found
   */
  getGroup (subSystemIndex, layerIndex, groupIndex) {
    const layer = this.getLayer(subSystemIndex, layerIndex)
    if (!layer) return null

    return layer.groups[parseInt(groupIndex)] || null
  }

  /**
   * Updates the state with a selected product for a group
   *
   * @param {number} groupIndex - Index of the group to update
   * @param {HTMLElement} productElement - Product element containing image and data
   * @param {HTMLSelectElement} selectElement - Select element with variant options
   */
  updateSystemBuilderState (groupIndex, productElement, selectElement) {
    const group = this.getGroup(
      this.subSystemIndex,
      this.layerIndex,
      groupIndex,
    )
    if (!group) return

    const image = productElement.querySelector('img')
    const variantId = selectElement.value
    const selectedOption = selectElement.options[selectElement.selectedIndex]
    const price = selectedOption?.dataset.systemBuilderVariantPrice

    const hotspotImage = productElement.dataset.systemBuilderImage

    group.productSelected = {
      variantId,
      price: price ? parseInt(price) : 0,
      imageUrl: image ? image.src : null,
      hotspotImageUrl: hotspotImage || null,
    }

    window.BAO.eventBus.dispatchEvent(
      window.BAO.EVENTS.SYSTEM_BUILDER.STATE_CHANGED,
      {
        subSystemIndex: this.subSystemIndex,
        layerIndex: this.layerIndex,
        groupIndex,
      },
    )
  }

  /**
   * Clears the selected product for a group
   *
   * @param {number} groupIndex - Index of the group to clear
   */
  clearSystemBuilderState (groupIndex) {
    const group = this.getGroup(
      this.subSystemIndex,
      this.layerIndex,
      groupIndex,
    )
    if (!group) return

    group.productSelected = null

    window.BAO.eventBus.dispatchEvent(
      window.BAO.EVENTS.SYSTEM_BUILDER.STATE_CHANGED,
      {
        subSystemIndex: this.subSystemIndex,
        layerIndex: this.layerIndex,
        groupIndex,
      },
    )
  }

  /**
   * Dismiss a tooltip for a specific layer
   *
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   */
  dismissTooltip (subSystemIndex, layerIndex) {
    const key = `${subSystemIndex}-${layerIndex}`
    this.dismissedTooltips.add(key)
  }

  /**
   * Check if a tooltip has been dismissed
   *
   * @param {number} subSystemIndex
   * @param {number} layerIndex
   * @returns {boolean}
   */
  isTooltipDismissed (subSystemIndex, layerIndex) {
    const key = `${subSystemIndex}-${layerIndex}`
    return this.dismissedTooltips.has(key)
  }
}

window.BAO.SystemBuilderState = SystemBuilderState
